import { useCallback } from 'react'
import { useFFEStore } from '../store/ffeStore'
import type { DocumentSummary } from '../services/storage/documents/types'
import { initialDocument } from '../store/slices/documentSlice'
import { CloudStorageService } from '../services/storage/documents/cloudStorage'
import { FFEDocument } from '../types/ffe'
import { App } from 'antd'
import { useNavigate } from 'react-router-dom'

export const replaceTagsInHeaderAndFooter = (doc: FFEDocument) => {
  doc.projectDescription = doc.projectDescription.replace('{today}', new Date().toLocaleDateString())
  doc.footerContent = doc.footerContent.replace('{today}', new Date().toLocaleDateString())
  return doc
}

export const useDocumentStorage = () => {
  const { message } = App.useApp()
  const document = useFFEStore((state) => state.document)
  const setLoadingDocument = useFFEStore((state) => state.setLoadingDocument)
  const setDocument = useFFEStore((state) => state.setDocument)
  const updateCloudId = useFFEStore((state) => state.updateCloudId)
  const storageService = new CloudStorageService()
  const navigate = useNavigate()
  const setErrorSaving = useFFEStore((state) => state.setErrorSaving)
  const adjustMargins = (doc: FFEDocument) => {
    // Calculate margin percentage
    doc.lineItems.forEach((item) => {
      if (item.margin) return
      const margin =
        (((item.sellPrice || 0) - (item.costPrice || 0)) /
          (item.costPrice || 1)) *
        100
      item.margin = margin
    })
    return doc
  }

  const saveDocument = useCallback(async () => {
    try {
      setLoadingDocument(true)
      const saveDocument = adjustMargins(document)
      const response = await storageService.saveDocument(saveDocument)
      setErrorSaving(false)
      if (document._id === undefined && response._id) {
        updateCloudId(response._id!)
        // add ID to the path of the location
        window.history.replaceState({}, '', `/app/editor/${response._id}`)
      }
      // on initial save, replace tags in header and footer
      if (!saveDocument._id) {
        const doc = replaceTagsInHeaderAndFooter(response)
        setDocument(doc)
      }
      setLoadingDocument(false)
      return response
    } catch (err) {
      console.error('Save error:', err)
      message.error('Failed to save document')
      setErrorSaving(true)
      setLoadingDocument(false)
    }
  }, [document, setLoadingDocument, updateCloudId, message])

  const loadDocument = useCallback(async (serverId: string) => {
    try {
      setLoadingDocument(true)
      const doc = await storageService.loadDocument(serverId)
      if (doc) {
        const docWithTags = replaceTagsInHeaderAndFooter(doc)
        setDocument(docWithTags)
        return doc
      }
    } catch (err) {
      console.error('Load error:', err)
      // redirect to the documents page
      navigate('/app/documents')
      message.error('Failed to load document')
    } finally {
      setLoadingDocument(false)
    }
  }, [setLoadingDocument, setDocument, message])

  const listDocuments = async () => {
    try {
      setLoadingDocument(true)
      return await storageService.listDocuments()
    } catch (err) {
      console.error('List error:', err)
      message.error('Failed to list documents')
      return [] as DocumentSummary[]
    } finally {
      setLoadingDocument(false)
    }
  }

  const deleteDocument = useCallback(async (serverId: string) => {
    try {
      setLoadingDocument(true)
      await storageService.deleteDocument(serverId)
    } catch (err) {
      console.error('Delete error:', err)
      message.error('Failed to delete document')
    } finally {
      setLoadingDocument(false)
    }
  }, [setLoadingDocument, message])

  const newDocument = () => {
    setDocument(initialDocument)
  }

  return {
    saveDocument,
    loadDocument,
    listDocuments,
    deleteDocument,
    newDocument
  }
}
